import React, { memo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Tooltip,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { PositionedScheduleItem } from '../utils/overlapLayout';
import { formatTime, formatDuration } from '../utils/timeUtils';
import { getScheduleLinkColors } from '../utils/dateTypeColors';
import { isRunningInWebView2, openWorkOrderInDesktop } from '../../common/Utils/webViewBridge';

interface ScheduleItemCardProps {
  item: PositionedScheduleItem;
  onClick?: (item: PositionedScheduleItem) => void;
  onDragStart?: (e: React.DragEvent, item: PositionedScheduleItem) => void;
  onDragEnd?: () => void;
  allowDrag?: boolean;
  onOpenWorkOrder?: (invoiceDocId: number) => void;
}

export const ScheduleItemCard: React.FC<ScheduleItemCardProps> = memo(({
  item,
  onClick,
  onDragStart,
  onDragEnd,
  allowDrag = true,
  onOpenWorkOrder,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const cardStyle = getScheduleLinkColors(item.workOrderId);
  
  const handleDragStart = (e: React.DragEvent) => {
    if (!allowDrag) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'schedule-item',
      scheduleItemId: item.scheduleItemId,
      startDateTime: item.startDateTime.toISOString(),
      endDateTime: item.endDateTime.toISOString(),
    }));
    onDragStart?.(e, item);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(item);
  };

  const handleNavigateToTechCalendar = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.woTechnicianId) return;
    
    const params = new URLSearchParams();
    params.set('technicianId', item.woTechnicianId.toString());
    // Format date as YYYY-MM-DD for the calendar
    const dateStr = item.startDateTime.toISOString().split('T')[0];
    params.set('date', dateStr);
    navigate(`/TechnicianCalendar?${params.toString()}`);
  };

  const handleOpenWorkOrder = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.workOrderId) return;
    
    const invoiceDocId = item.workOrderId;
    
    if (isRunningInWebView2()) {
      // Open in desktop app
      openWorkOrderInDesktop(invoiceDocId);
    } else {
      // Open in browser via callback
      onOpenWorkOrder?.(invoiceDocId);
    }
  };

  return (
    <Paper
      elevation={1}
      draggable={allowDrag}
      onDragStart={allowDrag ? handleDragStart : undefined}
      onDragEnd={allowDrag ? onDragEnd : undefined}
      onClick={handleClick}
      sx={{
        position: 'absolute',
        top: item.top ,
        left: `${item.left + 1}%`, // +1% for gap
        width: `${item.width - 2}%`, // -2% for gap on both sides
        height: item.height-6,
        bgcolor: cardStyle.bg,
        borderLeft: 3,
        borderColor: cardStyle.color,
        borderRadius: 1,
        p: 0.75,
        cursor: allowDrag ? 'grab' : 'pointer',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, transform 0.2s',
        zIndex: 1,
        '&:hover': {
          boxShadow: 4,
          zIndex: 10,
          transform: 'scale(1.02)',
        },
        '&:active': {
          cursor: allowDrag ? 'grabbing' : 'pointer',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Row 1: Title with WO badge and Calendar Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              color: cardStyle.color,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {item.title}
          </Typography>
          
          {/* Right side: WO Badge + Open WO Icon + Calendar Icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            {/* Work Order Document Number Badge */}
            {item.workOrderId && (
              <Tooltip title={`Work Order: ${item.workOrderNumber || 'N/A'}`}>
                <Chip
                  size="small"
                  label={item.workOrderNumber || 'WO'}
                  sx={{
                    height: 14,
                    fontSize: '0.55rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    color: theme.palette.success.dark,
                    borderColor: theme.palette.success.main,
                    '& .MuiChip-label': {
                      px: 0.5,
                    },
                  }}
                  variant="outlined"
                />
              </Tooltip>
            )}

            {/* Open Work Order Icon - only show when work order exists */}
            <Tooltip title={item.workOrderId ? "Open Work Order" : "No work order to open"}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenWorkOrder}
                  disabled={!item.workOrderId}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    color: item.workOrderId 
                      ? theme.palette.info.main 
                      : theme.palette.action.disabled,
                    '&:hover': {
                      bgcolor: item.workOrderId 
                        ? alpha(theme.palette.info.main, 0.1)
                        : 'transparent',
                    },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </span>
            </Tooltip>
            
            {/* Tech Calendar V2 Navigation Icon - only show when technician selected AND work order saved */}
            <Tooltip title={
              !item.workOrderId 
                ? "Save work order first" 
                : !item.woTechnicianId 
                  ? "No technician assigned" 
                  : "View in Tech Calendar V2"
            }>
              <span>
                <IconButton
                  size="small"
                  onClick={handleNavigateToTechCalendar}
                  disabled={!item.woTechnicianId || !item.workOrderId}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    color: item.woTechnicianId && item.workOrderId 
                      ? theme.palette.primary.main 
                      : theme.palette.action.disabled,
                    '&:hover': {
                      bgcolor: item.woTechnicianId && item.workOrderId 
                        ? alpha(theme.palette.primary.main, 0.1)
                        : 'transparent',
                    },
                  }}
                >
                  <CalendarMonthIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Row 2: Time and Customer Number in same row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          {/* Time */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
            <AccessTimeIcon sx={{ fontSize: 10, color: 'text.secondary', flexShrink: 0 }} />
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.6rem', 
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {formatTime(item.startDateTime)} - {formatDuration(item.startDateTime, item.endDateTime)}
            </Typography>
          </Box>
          
          {/* Customer Name (truncated) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, maxWidth: '50%' }}>
            <PersonIcon sx={{ fontSize: 10, color: 'text.secondary', flexShrink: 0 }} />
            <Tooltip title={item.customerName || ''}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  color: 'text.primary',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 70,
                }}
              >
                {item.customerName && item.customerName.length > 10 
                  ? `${item.customerName.substring(0, 10)}...` 
                  : item.customerName || ''}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {/* Row 3: Segment Title (Agenda) - if available */}
        {item.segmentTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <AssignmentIcon sx={{ fontSize: 10, color: 'text.secondary', flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.segmentTitle}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
});

ScheduleItemCard.displayName = 'ScheduleItemCard';
